import { Chart, Plugin } from 'chart.js';
import { Utils } from '../../../shared/utils';

/**
 * Paleta de charts de /resume. Ancla en los tokens de marca de la app
 * (ver "Marca y theming" en CLAUDE.md) pero usa `$cf-brand-dark` en vez de
 * `$cf-brand` como color de serie: el amber plano (#e3fd64) es demasiado claro
 * para leerse como trazo/relleno sobre fondo blanco (falla el contraste 3:1 de
 * chart.js), mientras que su variante oscura mantiene la identidad de marca y
 * sí es legible como marca de datos.
 */
export const chartPalette = {
  brand: '#4d7a00',    // Acento de marca en gráficas — $cf-brand-dark
  income: '#4d7a00',   // Ingresos — $cf-brand-dark
  grant: '#2a78d6',    // Subvenciones — azul categórico
  expense: '#dc2626',  // Gastos — $cf-error
  good: '#16a34a',     // Cobrado / Pagado — $cf-success
  warning: '#d97706',  // No cobrado — $cf-warning
  bad: '#dc2626',      // No pagado — $cf-error
  textMuted: '#6b7280',
  textPrimary: '#1a1d23',
  grid: '#eef0f2',
  surface: '#ffffff',
};

/**
 * Paleta categórica para gráficas de N series (p.ej. reparto por categoría en
 * un pie/doughnut) donde no aplica el significado semántico de `chartPalette`
 * (ingreso/gasto/cobrado/pendiente). Orden fijo, validado CVD-safe.
 */
export const categoricalPalette = [
  '#2a78d6', // azul
  '#eb6834', // naranja
  '#1baf7a', // aqua
  '#eda100', // amarillo
  '#e87ba4', // magenta
  '#008300', // verde
  '#4a3aa7', // violeta
  '#e34948', // rojo
];

export const chartFontFamily = "'Inter Tight', 'Helvetica Neue', Arial, sans-serif";

export function euro(value: number): string {
  const formatted = Utils.FormatMoney(value ?? 0);
  return formatted === '' ? '0,00 €' : `${formatted} €`;
}

function hexToRgb(hex: string): string {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean, 16);
  return `${(bigint >> 16) & 255},${(bigint >> 8) & 255},${bigint & 255}`;
}

/** Relleno en degradado vertical para barras/áreas — sustituye el color plano. */
export function gradientFill(colorHex: string, topAlpha = 0.85, bottomAlpha = 0.08) {
  const rgb = hexToRgb(colorHex);
  return (context: any) => {
    const chart = context.chart;
    const { ctx, chartArea } = chart;
    if (!chartArea) return `rgba(${rgb},${topAlpha})`;
    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, `rgba(${rgb},${topAlpha})`);
    gradient.addColorStop(1, `rgba(${rgb},${bottomAlpha})`);
    return gradient;
  };
}

export function tooltipBase(): any {
  return {
    enabled: true,
    backgroundColor: '#1a1d23',
    titleColor: '#ffffff',
    bodyColor: 'rgba(255,255,255,0.85)',
    borderColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    padding: 10,
    cornerRadius: 8,
    displayColors: true,
    boxPadding: 4,
    titleFont: { family: chartFontFamily, weight: 600, size: 12 },
    bodyFont: { family: chartFontFamily, size: 12 },
    callbacks: {
      label: (ctx: any) => {
        const label = ctx.dataset?.label ? ctx.dataset.label + ': ' : '';
        const raw = typeof ctx.raw === 'number' ? ctx.raw : (ctx.parsed?.y ?? ctx.parsed ?? 0);
        return label + euro(raw);
      }
    }
  };
}

export function legendBase(): any {
  return {
    display: true,
    position: 'top',
    align: 'end',
    labels: {
      usePointStyle: true,
      pointStyle: 'circle',
      boxWidth: 8,
      boxHeight: 8,
      padding: 16,
      color: chartPalette.textMuted,
      font: { family: chartFontFamily, size: 12, weight: 500 }
    }
  };
}

export function barScalesBase(): any {
  return {
    x: {
      grid: { display: false },
      ticks: { color: chartPalette.textMuted, font: { family: chartFontFamily, size: 11 } },
      border: { display: false }
    },
    y: {
      beginAtZero: true,
      grid: { color: chartPalette.grid, drawTicks: false },
      ticks: {
        color: chartPalette.textMuted,
        font: { family: chartFontFamily, size: 11 },
        maxTicksLimit: 6,
        callback: (value: number) => Utils.FormatMoney(value, 0) + ' €'
      },
      border: { display: false }
    }
  };
}

/** Variante de `barScalesBase()` para barras horizontales (`indexAxis: 'y'`):
 *  el eje de valor (x) lleva el formato € y el eje de categorías (y) no tiene grid. */
export function horizontalBarScalesBase(): any {
  return {
    x: {
      beginAtZero: true,
      grid: { color: chartPalette.grid, drawTicks: false },
      ticks: {
        color: chartPalette.textMuted,
        font: { family: chartFontFamily, size: 11 },
        maxTicksLimit: 6,
        callback: (value: number) => Utils.FormatMoney(value, 0) + ' €'
      },
      border: { display: false }
    },
    y: {
      grid: { display: false },
      ticks: { color: chartPalette.textMuted, font: { family: chartFontFamily, size: 11 } },
      border: { display: false }
    }
  };
}

/** ≤24px de grosor, extremo redondeado, hueco de 2px entre barras adyacentes. */
export const barGeometry = {
  borderRadius: 6,
  borderSkipped: false as const,
  maxBarThickness: 22,
  categoryPercentage: 0.62,
  barPercentage: 0.9,
};

export function lineGeometryFor(colorHex: string) {
  return {
    borderColor: colorHex,
    borderWidth: 2,
    pointRadius: 0,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: colorHex,
    pointHoverBorderColor: '#ffffff',
    pointHoverBorderWidth: 2,
    tension: 0.35,
    fill: true,
  };
}

/**
 * Plugin global de chart.js: pinta un valor + etiqueta en el centro de un
 * doughnut cuando `options.plugins.centerText` está presente. Se registra una
 * sola vez (import-time) y no afecta a charts que no declaren esa opción.
 */
export const centerTextPlugin: Plugin<'doughnut'> = {
  id: 'centerText',
  beforeDraw(chart) {
    const opts: any = (chart.options.plugins as any)?.centerText;
    if (!opts?.text) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    const holeDiameter = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) * 0.62;

    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Encoge la fuente hasta que el texto quepa dentro del hueco del doughnut — nunca se recorta.
    let fontSize = 20;
    ctx.font = `700 ${fontSize}px ${chartFontFamily}`;
    while (ctx.measureText(opts.text).width > holeDiameter && fontSize > 11) {
      fontSize -= 1;
      ctx.font = `700 ${fontSize}px ${chartFontFamily}`;
    }
    ctx.fillStyle = opts.color || chartPalette.textPrimary;
    ctx.fillText(opts.text, centerX, centerY + (opts.subtext ? -(fontSize * 0.42) : 0));

    if (opts.subtext) {
      ctx.font = `500 11px ${chartFontFamily}`;
      ctx.fillStyle = chartPalette.textMuted;
      ctx.fillText(opts.subtext, centerX, centerY + fontSize * 0.6);
    }
    ctx.restore();
  }
};

Chart.register(centerTextPlugin);

export function doughnutOptionsBase(centerText?: { text: string; subtext?: string; color?: string }): any {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    spacing: 3,
    animation: { animateRotate: true, duration: 700, easing: 'easeOutQuart' },
    plugins: {
      legend: legendBase(),
      tooltip: tooltipBase(),
      centerText: centerText ?? null,
    }
  };
}
